import * as React from "react";
import {
    CommandBar,
    ContextualMenuItemType,
    ICommandBarItemProps,
    IContextualMenuItem,
    IButtonProps,
} from "office-ui-fabric-react";
import { NewformaToastIntent } from "../../_base/Toast";
import { LocalizeContextProps, withLocalize } from "react-localize-redux";
import { OfficeRoamingSettings } from "../../../services/OfficeRoamingSettings";
import { AnalyticsActionType, AnalyticsCategoryType, AnalyticsManager } from "../../../services/AnalyticsManager";
import SettingsComponent from "../../shared/settings/SettingsComponent";
import { ConfigurationService } from "../../../services/ConfigurationService";
import { MailboxItem, OfficeWrapper } from "../../../services/OfficeWrapper";
import { Authenticator } from "../../../services/Authenticator";
import "./AppHeaderComponent.less";
import { Logger, Log } from "../../../services/Logger";
import { CustomerSupportEmailService } from "../../../services/CustomerSupportEmailService";
import { WindowWrapper } from "../../../services/WindowWrapper";
import SearchComponent from "../../search/SearchComponent";
import { SmartFilingManager } from "../../../services/SmartFiling/SmartFilingManager";
import { IProjectsService } from "../../../services/NewformaApi/IProjectsService";
import { EmailApiService } from "../../../services/NewformaApi/EmailApiService";
import AsyncResult = Office.AsyncResult;
import Dialog = Office.Dialog;
import ProjectTeamComponent from "../../projectTeam/ProjectTeamComponent";
import { SendAndFileHelpers } from "../../../helpers/SendAndFile/SendAndFileHelpers";
import { FormValidationHelpers } from "../../../helpers/FormValidationHelpers";
import { MsGraphApiService } from "../../../services/MsGraphApiService";
import { NrnServiceWrapper } from "../../../services/NrnServiceWrapper";
import { SubmittalsApiService } from "../../../services/NewformaApi/SubmittalsApiService";
import { ConfigurationsApiService } from "../../../services/NewformaApi/ConfigurationsApiService";
import { StorageWrapper } from "../../../services/StorageWrapper";
import { DateHelpers } from "../../../helpers/DateHelpers";
import { RfiApiService } from "../../../services/NewformaApi/RfiApiService";
import { InvalidateCacheService } from "../../../services/NewformaApi/InvalidateCacheService";
import { FileTransferApiService } from "../../../services/NewformaApi/FileTransferApiService";
import { TeachingBubble, ITeachingBubbleStyles, DirectionalHint } from "office-ui-fabric-react";
import { OutlookApiService } from "../../../services/OutlookApiService";
import { SharePointAuthServiceFactory } from "../../../services/SharePoint/SharePointAuthServiceFactory";
import { ProjectsProvider } from "../../../components/shared/projectPicker/ProjectsContext";
import { SmartFilingManagerFluent9 } from "@src/services/SmartFiling/SmartFilingManagerFluent9";
import FileTransferFluent9Component from "../../../components/fileTransfer/FileTransferFluent9Component";
import { FluentProvider } from "@fluentui/react-components";
import { LightDarkContext } from "../LightDarkProvider";

export interface AppHeaderProps extends LocalizeContextProps {
    loggedIn: boolean;
    onLogOut: () => void;
    analyticsManager: AnalyticsManager;
    configService: ConfigurationService;
    officeRoamingSettings: OfficeRoamingSettings;
    officeWrapper: OfficeWrapper;
    authenticator: Authenticator;
    logger: Logger;
    customerSupportEmailService: CustomerSupportEmailService;
    windowWrapper: WindowWrapper;
    onSettingsChanged: () => void;
    isEulaSupported: boolean;
    smartFilingManager: SmartFilingManager;
    smartFilingManagerFluent9: SmartFilingManagerFluent9;
    mailboxItem: MailboxItem | null;
    projectsService: IProjectsService;
    emailApiService: EmailApiService;
    onExpiredSession: () => void;
    sendAndFileHelpers: SendAndFileHelpers;
    onShowToast: (message: string | null, type: NewformaToastIntent) => void;
    onDismissToast: () => void;
    formValidationHelpers: FormValidationHelpers;
    nrnServiceWrapper: NrnServiceWrapper;
    msGraphApiService: MsGraphApiService;
    configurationService: ConfigurationsApiService;
    submittalsApiService: SubmittalsApiService;
    storageWrapper: StorageWrapper;
    dateHelpers: DateHelpers;
    rfiApiService: RfiApiService;
    invalidateCacheService: InvalidateCacheService;
    fileTransferApiService: FileTransferApiService;
    outlookApiService: OutlookApiService;
    sharePointAuthServiceFactory?: SharePointAuthServiceFactory;
    showFilesTransferTeachingBubble: boolean;
    shouldShowMultipleEmailsTeachingBubbleAndInvalidateCache: boolean;
    onDismissTeachingBubble: () => void;
    showFileTransferButton: boolean;
    showProjectItemTeachingBubble: boolean;
    onDismissProjectItemTeachingBubble: () => void;
    isFilePathInAttachmentsSupported: boolean;
    toastMessageFromParent?: string | null;
    hideNoProjectsToast: () => void;
    shouldShowFileTransferPanel: (shouldShow: boolean) => void;
    shouldShowFileTransferView: boolean;
    hideSearchTeamBtns?: boolean;
    onMicrosoft365EmailToggleChanged: (toggleValue: boolean) => void;
    isMicrosoft365EmailFeatureEnabled: boolean;
    isSendFilesFromProjectSupported: boolean;
    isGlobalProjectSearchSupported: boolean;
    useFluent9Surface?: boolean;
}

export interface AppHeaderState {
    hideApplicationSettingsDialog: boolean;
    showSettingsPanel: boolean;
    showSearchPanel: boolean;
    showProjectTeamPanel: boolean;
    showFileTransferPanel: boolean;
    toastText?: string;
    toastType?: NewformaToastIntent;
    hideSearchTeamBtns?: boolean;
}

class AppHeaderComponent extends React.Component<AppHeaderProps, AppHeaderState> {
    static contextType = LightDarkContext;
    declare context: React.ContextType<typeof LightDarkContext>;
    constructor(props: Readonly<AppHeaderProps>, context: any) {
        super(props, context);

        this.state = {
            hideApplicationSettingsDialog: true,
            showSettingsPanel: false,
            showSearchPanel: false,
            showProjectTeamPanel: false,
            showFileTransferPanel: false,
            hideSearchTeamBtns: this.props.hideSearchTeamBtns,
        };
    }

    async componentDidUpdate(prevProps: Readonly<AppHeaderProps>, prevState: Readonly<AppHeaderState>) {
        if (
            this.props.toastMessageFromParent === null &&
            this.props.toastMessageFromParent !== prevProps.toastMessageFromParent
        ) {
            this.setState({ toastText: "", toastType: undefined });
        }

        // NOTE: mailboxItem changes are detected/owned by AppComponent (which registers a single,
        // resilient ItemChanged handler for the whole app - see AppComponent.itemChanged). We react to
        // the resulting prop change here instead of registering our own separate Office.js handler, to
        // avoid multiple independent (and independently unreliable) ItemChanged registrations.
        if (this.props.mailboxItem && prevProps.mailboxItem !== this.props.mailboxItem) {
            this.props.logger.info("Selected mail item changed");
            this.setState({ showSettingsPanel: false, showSearchPanel: false });
        }
    }

    openSettingsClicked() {
        this.props.logger.info("Settings panel opened");
        this.recordAnalyticsForUserAction(AnalyticsActionType.UserMenuSettingsOpened);
        this.setState({ showSettingsPanel: true });
    }

    private closeSettingsClicked() {
        this.props.logger.info("Settings panel closed");
        this.setState({ showSettingsPanel: false });
    }

    openSearchClicked() {
        this.props.logger.info("Search panel opened");
        this.recordAnalyticsForUserAction(AnalyticsActionType.UserMenuSearch);
        this.setState({ showSearchPanel: true });
    }

    private closeSearchClicked() {
        this.props.logger.info("Search panel closed");
        this.setState({ showSearchPanel: false });
    }

    private openFileTransfersClicked() {
        this.props.logger.info("File Transfer panel opened");
        this.recordAnalyticsForUserAction(AnalyticsActionType.UserMenuSearch);
        this.setState({ showFileTransferPanel: true });
        this.props.shouldShowFileTransferPanel(true);
        this.props.hideNoProjectsToast();
    }

    private closeFileTransferClicked() {
        this.props.logger.info("File Transfer panel closed");
        this.setState({ showFileTransferPanel: false });
        this.props.shouldShowFileTransferPanel(false);
    }

    openProjectTeamMembersClicked() {
        this.props.logger.info("Project Team panel opened");
        this.recordAnalyticsForUserAction(AnalyticsActionType.ProjectTeamViewed);
        this.setState({ showProjectTeamPanel: true });
    }

    private closeProjectTeamMembersClicked() {
        this.props.logger.info("Project Team panel closed");
        this.setState({ showProjectTeamPanel: false });
    }

    async signOutClicked(): Promise<void> {
        this.recordAnalyticsForUserAction(AnalyticsActionType.Logout);
        await this.props.officeRoamingSettings.logoutFromApp();
        this.props.authenticator.logoutFromApp();
        this.setState({ hideApplicationSettingsDialog: true });
        this.props.onLogOut();
    }

    onCustomerSupportClicked(): void {
        this.recordAnalyticsForUserAction(AnalyticsActionType.UserMenuCustomerSupport);
        const body = [
            this.props.translate("APP.MISCELLANEOUS.CUSTOMER_SUPPORT_EMAIL_BODY"),
            `${this.props.translate("APP.MISCELLANEOUS.REVISION_NUMBER")}: ${__COMMIT_HASH__}`,
            `${this.props.translate("APP.MISCELLANEOUS.MANIFEST_ID")}: ${__MANIFEST_ID__}`,
        ].join("\n");

        const subject = this.props.customerSupportEmailService.getSubject();
        const supportEmail = this.props.customerSupportEmailService.getSupportEmailAddress();
        const url = `mailto:${supportEmail}?subject=${subject}&body=${encodeURIComponent(body)}`;

        const res = [
            this.props.customerSupportEmailService.getDiagnostics(),
            `${this.props.translate("APP.MISCELLANEOUS.REVISION_NUMBER")}: ${__COMMIT_HASH__}`,
            `${this.props.translate("APP.MISCELLANEOUS.MANIFEST_ID")}: ${__MANIFEST_ID__}`,
        ].join("\n");
        this.props.windowWrapper.copyTextToClipboard(res);

        const isClassicOutlook =
            Office.context && (Office.context.platform === Office.PlatformType.PC || Office.context.platform == null);

        const isBrowserOutlook = Office.context && Office.context.platform === Office.PlatformType.OfficeOnline;

        if (isClassicOutlook) {
            this.props.officeWrapper.displayDialogAsync(url, {}, (asyncResult: AsyncResult<Dialog>) => {
                this.props.windowWrapper.open(url);
                const dialog = asyncResult.value;
                dialog.close();
            });
        } else if (
            isBrowserOutlook &&
            Office.context.mailbox &&
            typeof Office.context.mailbox.displayNewMessageForm === "function"
        ) {
            Office.context.mailbox.displayNewMessageForm({
                toRecipients: [supportEmail],
                subject,
                htmlBody: body.replace(/\n/g, "<br/>"),
            });
        } else {
            this.props.windowWrapper.open(url);
        }
    }

    private openFeedbackMenuItem(): ICommandBarItemProps {
        return {
            key: "feedback",
            iconProps: {
                iconName: "Feedback",
            },
            iconOnly: true,
            ["data-automation-id"]: "feedbackButton",
            name: this.props.translate("APP.COMMANDBAR.FEEDBACK") as string,
            href: this.props.translate("URLS.FEEDBACK") as string,
            target: "_blank",
            onClick: this.recordAnalyticsForUserAction.bind(this, AnalyticsActionType.UserMenuFeedback),
        };
    }

    private openProjectTeamMenuItem(): ICommandBarItemProps {
        return {
            key: "projectTeam",
            name: this.props.translate("APP.COMMANDBAR.PROJECT_TEAM") as string,
            iconProps: {
                iconName: "ContactList",
            },
            iconOnly: true,
            onClick: this.openProjectTeamMembersClicked.bind(this),
        };
    }

    private getOverflowMenuItem(): ICommandBarItemProps {
        return {
            key: "menuOptions",
            iconProps: {
                iconName: "More",
            },
            name: this.props.translate("APP.COMMANDBAR.OPTIONS") as string,
            iconOnly: true,
            ["data-automation-id"]: "overflowMenuButton",
            subMenuProps: {
                items: this.props.loggedIn
                    ? this.getOptionsMenuItemsAuthenticated()
                    : this.getOptionsMenuItemsUnauthenticated(),
            },
        };
    }

    private openSearchItem(): ICommandBarItemProps {
        return {
            key: "search",
            iconOnly: true,
            iconProps: {
                iconName: "Search",
            },
            className: "searchItem",
            name: this.props.translate("APP.COMMANDBAR.SEARCH") as string,
            onClick: this.openSearchClicked.bind(this),
            ["data-automation-id"]: "searchButton",
        };
    }

    private openFileTransfersItem(): ICommandBarItemProps {
        return {
            key: "fileTransfers",
            iconOnly: true,
            iconProps: {
                iconName: "Switch",
            },
            className: "fileTransfersItem",
            name: this.props.translate("APP.COMMANDBAR.FILE_TRANSFER") as string,
            onClick: this.openFileTransfersClicked.bind(this),
            ["data-automation-id"]: "fileTransfersButton",
        };
    }

    private openFileTransfersItemFluentUI9(): ICommandBarItemProps {
        return {
            key: "fileTransfersFluentUI9",
            iconOnly: true,
            iconProps: {
                iconName: "Switch",
            },
            className: "fileTransfersItem",
            name: this.props.translate("APP.COMMANDBAR.FILE_TRANSFER") as string,
            onClick: () => {
                this.openFileTransfersClicked();
            },
            ["data-automation-id"]: "fileTransfersButton",
        };
    }

    private openNewformaItem(): IContextualMenuItem {
        return {
            key: "openNewforma",
            name: this.props.translate("APP.COMMANDBAR.OPEN_NEWFORMA_WEB_APP") as string,
            iconProps: {
                iconName: "OpenInNewWindow",
            },
            ["data-automation-id"]: "openNewFormaButton",
            title: this.props.translate("APP.COMMANDBAR.OPEN_NEWFORMA_WEB_APP_TOOLTIP") as string,
            href: this.props.configService.webAppURL,
            target: "_blank",
        };
    }

    private customerSupportItem(): IContextualMenuItem {
        return {
            key: "customerSupport",
            name: this.props.translate("APP.COMMANDBAR.CUSTOMER_SUPPORT") as string,
            iconProps: {
                iconName: "NewMail",
            },
            onClick: this.onCustomerSupportClicked.bind(this),
            target: "_blank",
        };
    }

    private eulaMenuItem(): IContextualMenuItem {
        return {
            key: "eula",
            name: this.props.translate("APP.COMMANDBAR.EULA") as string,
            iconProps: {
                iconName: "EntitlementRedemption",
            },
            href: this.props.translate("URLS.EULA") as string,
            target: "_blank",
            onClick: this.recordAnalyticsForUserAction.bind(this, AnalyticsActionType.UserMenuEula),
        };
    }

    private dividerMenuItem(): IContextualMenuItem {
        return {
            key: "divider",
            itemType: ContextualMenuItemType.Divider,
        };
    }

    private settingsMenuItem(): IContextualMenuItem {
        return {
            key: "settings",
            name: this.props.translate("APP.COMMANDBAR.SETTINGS") as string,
            iconProps: {
                iconName: "Settings",
            },
            onClick: this.openSettingsClicked.bind(this),
        };
    }

    private revisionMenuItem(): IContextualMenuItem {
        return {
            key: "revision",
            name: `${this.props.translate("APP.COMMANDBAR.REVISION")}: ${__COMMIT_HASH__}`,
        };
    }

    private branchMenuItem(): IContextualMenuItem {
        return {
            key: "branch",
            name: `${this.props.translate("APP.COMMANDBAR.BRANCH")}: ${__BRANCH__}`,
        };
    }

    private versionMenuItem(): IContextualMenuItem {
        return {
            key: "version",
            name: `${this.props.translate("APP.COMMANDBAR.VERSION")}: ${this.props.configService.version}`,
        };
    }

    private logOutMenuItem(): IContextualMenuItem {
        return {
            key: "logOut",
            name: this.props.translate("APP.COMMANDBAR.SIGN_OUT") as string,
            iconProps: {
                iconName: "Unlock",
            },
            onClick: this.signOutClicked.bind(this) as () => void,
        };
    }

    private helpMenuItem(): IContextualMenuItem {
        return {
            key: "help",
            name: this.props.translate("APP.COMMANDBAR.HELP") as string,
            iconProps: {
                iconName: "Help",
            },
            href: this.props.translate("URLS.HELP") as string,
            target: "_blank",
            onClick: this.recordAnalyticsForUserAction.bind(this, AnalyticsActionType.UserMenuHelp),
        };
    }

    private getOptionsMenuItemsUnauthenticated(): IContextualMenuItem[] {
        const menuItems: IContextualMenuItem[] = [];

        menuItems.push(this.openNewformaItem());
        menuItems.push(this.customerSupportItem());
        if (this.props.isEulaSupported) {
            menuItems.push(this.eulaMenuItem());
        }
        menuItems.push(this.dividerMenuItem());
        menuItems.push(this.versionMenuItem());

        if (__ENV__ !== "prod") {
            menuItems.push(this.branchMenuItem());
            menuItems.push(this.revisionMenuItem());
        }

        return menuItems;
    }

    private getOptionsMenuItemsAuthenticated(): IContextualMenuItem[] {
        const menuItems: IContextualMenuItem[] = [];

        menuItems.push(this.openNewformaItem());
        menuItems.push(this.logOutMenuItem());
        menuItems.push(this.dividerMenuItem());
        menuItems.push(this.settingsMenuItem());
        menuItems.push(this.openFeedbackMenuItem());
        menuItems.push(this.customerSupportItem());
        if (this.props.isEulaSupported) {
            menuItems.push(this.eulaMenuItem());
        }
        menuItems.push(this.helpMenuItem());
        menuItems.push(this.dividerMenuItem());
        menuItems.push(this.versionMenuItem());

        if (__ENV__ !== "prod") {
            menuItems.push(this.branchMenuItem());
            menuItems.push(this.revisionMenuItem());
        }

        return menuItems;
    }

    private recordAnalyticsForUserAction(action: AnalyticsActionType): void {
        this.props.analyticsManager.recordEvent(AnalyticsCategoryType.UserActions, action);
    }

    private getFarItems(): ICommandBarItemProps[] {
        const commandBarProps = [];
        if (this.props.loggedIn) {
            if (!this.state.hideSearchTeamBtns) {
                commandBarProps.push(this.openSearchItem());
                if (this.props.showFileTransferButton) {
                    commandBarProps.push(this.openFileTransfersItemFluentUI9());
                }
            }
        } else {
            commandBarProps.push(this.openFeedbackMenuItem());
        }
        commandBarProps.push(this.getOverflowMenuItem());
        return commandBarProps;
    }

    private get styles(): Partial<ITeachingBubbleStyles> {
        return {
            footer: {
                display: "flex",
                flexDirection: "row-reverse",
                [`& > .ms-StackItem > span`]: {
                    marginRight: "75px",
                },
                color: this.context.isLightMode ? "white" : "black",
            },
            content: {
                color: this.context.isLightMode ? "white" : "black",
            },
            subText: {
                color: this.context.isLightMode ? "white" : "black",
            },
            headline: {
                color: this.context.isLightMode ? "white" : "black",
            },
            closeButton: {
                paddingBottom: "2.5px",
                [`.ms-Icon`]: {
                    color: this.context.isLightMode ? "white" : "black",
                },
            },
            root: {
                marginRight: "15px",
            },
            subComponentStyles: {
                callout: {
                    beak: {
                        marginRight: "-15px",
                    },
                },
            },
        };
    }

    private teachingBubbleButtonProps(buttonText: string): IButtonProps {
        return {
            children: buttonText,
            onClick: () => this.props.onDismissTeachingBubble(),
        };
    }

    private renderFilesTransferTeachingBubble() {
        if (
            !this.props.showFileTransferButton ||
            !this.props.showFilesTransferTeachingBubble ||
            this.props.shouldShowMultipleEmailsTeachingBubbleAndInvalidateCache
        ) {
            return null;
        }
        return (
            <TeachingBubble
                calloutProps={{ directionalHint: DirectionalHint.bottomLeftEdge }}
                target=".fileTransfersItem"
                isWide={false}
                hasCloseButton={true}
                onDismiss={() => this.props.onDismissTeachingBubble()}
                primaryButtonProps={this.teachingBubbleButtonProps(
                    this.props.translate("TEACHING_BUBBLES_v1.BUBBLE_INFO.PART_3") as string
                )}
                closeButtonAriaLabel={this.props.translate("TEACHING_BUBBLES_v1.BUBBLE_INFO.PART_3") as string}
                headline={this.props.translate("TEACHING_BUBBLES_v1.FILE_TRANSFER_BUBBLE.PART_1") as string}
                styles={this.styles}
                ignoreExternalFocusing={true}
            >
                <span className="TeachingBubble-h-2">
                    {this.props.translate("TEACHING_BUBBLES_v1.FILE_TRANSFER_BUBBLE.PART_2") as string}
                </span>
            </TeachingBubble>
        );
    }

    private renderProjectItemTeachingBubble() {
        const isShowingFileTransferBubble =
            this.props.showFileTransferButton && this.props.showFilesTransferTeachingBubble;
        if (
            this.props.shouldShowMultipleEmailsTeachingBubbleAndInvalidateCache ||
            isShowingFileTransferBubble ||
            !this.props.showProjectItemTeachingBubble
        ) {
            return null;
        }
        const color = this.context.isLightMode ? "white" : "black";
        const imgSrc = `./assets/project-item-teaching-bubble-${this.context.isLightMode ? "light" : "dark"}.gif`;
        const headline = String(this.props.translate("TEACHING_BUBBLES_v1.PROJECT_ITEM_BUBBLE.HEADLINE"));
        const [content1, content2, content3] = String(
            this.props.translate("TEACHING_BUBBLES_v1.PROJECT_ITEM_BUBBLE.PARAGRAPH_MIDDLE")
        ).split('"');
        const styles: Partial<ITeachingBubbleStyles> = {
            content: {
                color,
            },
            subText: {
                color,
            },
            headline: {
                color,
            },
            closeButton: {
                [`.ms-Icon`]: {
                    color,
                },
            },
        };

        return (
            <TeachingBubble
                calloutProps={{
                    className: "custom-bubble-callout",
                    directionalHint: DirectionalHint.topCenter,
                    gapSpace: 10,
                    preventDismissOnScroll: true,
                }}
                target="#nav-dropdown-bubble-target"
                isWide={false}
                hasCloseButton={true}
                closeButtonAriaLabel={this.props.translate("TEACHING_BUBBLES_v1.BUBBLE_INFO.PART_3") as string}
                onDismiss={this.props.onDismissProjectItemTeachingBubble}
                headline={headline}
                styles={styles}
                ignoreExternalFocusing={true}
            >
                <p>
                    {content1}"<span style={{ fontWeight: "bold" }}>{content2}</span>"{content3}
                </p>
                <img
                    src={imgSrc}
                    alt={String(this.props.translate("TEACHING_BUBBLES_v1.PROJECT_ITEM_BUBBLE.ALT"))}
                    style={{
                        display: "block",
                        maxWidth: "100%",
                        marginTop: "1em",
                    }}
                />
            </TeachingBubble>
        );
    }

    renderTextFromFileTransferComp(toastText: string, toastType: NewformaToastIntent) {
        this.setState({ toastText: toastText, toastType: toastType });
        this.props.onShowToast(toastText, toastType);
    }

    onDismissAndClearToastMessageAndType() {
        this.setState({ toastText: "", toastType: undefined });
        this.props.onDismissToast();
    }

    render(): JSX.Element {
        const appHeaderClassName = this.props.useFluent9Surface
            ? "newforma-appHeader newforma-appHeader--fluent9"
            : "newforma-appHeader";

        return (
            <div id="AppHeader" className={appHeaderClassName}>
                <CommandBar
                    key={Math.random()}
                    items={[]}
                    farItems={this.getFarItems()}
                    className="newforma-commandBar"
                />

                <SettingsComponent
                    onDismiss={this.closeSettingsClicked.bind(this)}
                    showSettingsPanel={this.state.showSettingsPanel}
                    officeRoamingSettings={this.props.officeRoamingSettings}
                    officeWrapper={this.props.officeWrapper}
                    logger={this.props.logger}
                    onSettingsChanged={this.props.onSettingsChanged}
                    analyticsManager={this.props.analyticsManager}
                    storageWrapper={this.props.storageWrapper}
                    invalidateCacheService={this.props.invalidateCacheService}
                    onShowToast={this.props.onShowToast}
                    onDismissToastMessage={this.onDismissAndClearToastMessageAndType.bind(this)}
                    toastMessage={this.state.toastText}
                    toastType={this.state.toastType}
                    onMicrosoft365EmailToggleChanged={this.props.onMicrosoft365EmailToggleChanged}
                    isMicrosoft365EmailFeatureEnabled={this.props.isMicrosoft365EmailFeatureEnabled}
                />
                <SearchComponent
                    onDismiss={this.closeSearchClicked.bind(this)}
                    showSearchPanel={this.state.showSearchPanel}
                    logger={this.props.logger}
                    smartFilingManager={this.props.smartFilingManager}
                    mailboxItem={this.props.mailboxItem}
                    projectsService={this.props.projectsService}
                    configService={this.props.configService}
                    isLoggedIn={this.props.loggedIn}
                    onExpiredSession={this.props.onExpiredSession}
                    analyticsManager={this.props.analyticsManager}
                    onShowToast={this.props.onShowToast}
                    onDismissToastMessage={this.onDismissAndClearToastMessageAndType.bind(this)}
                    toastMessage={this.state.toastText}
                    toastType={this.state.toastType}
                />
                <ProjectTeamComponent
                    logger={this.props.logger}
                    showPanel={this.state.showProjectTeamPanel}
                    onDismiss={this.closeProjectTeamMembersClicked.bind(this)}
                    projectsService={this.props.projectsService}
                    smartFilingManager={this.props.smartFilingManager}
                    officeWrapper={this.props.officeWrapper}
                    mailBoxItem={this.props.mailboxItem}
                    onExpiredSession={this.props.onExpiredSession}
                    sendAndFileHelpers={this.props.sendAndFileHelpers}
                    analyticsManager={this.props.analyticsManager}
                    onShowToast={this.props.onShowToast}
                />
                {/* FluentProvider and ProjectsProvider should eventually wrap AppComponent */}
                <FluentProvider theme={this.context.theme}>
                    <ProjectsProvider logger={this.props.logger}>
                        <FileTransferFluent9Component
                            outlookApiService={this.props.outlookApiService}
                            fileTransferApiService={this.props.fileTransferApiService}
                            sharePointAuthServiceFactory={this.props.sharePointAuthServiceFactory}
                            smartFilingManager={this.props.smartFilingManagerFluent9}
                            invalidateCacheService={this.props.invalidateCacheService}
                            projectsService={this.props.projectsService}
                            officeWrapper={this.props.officeWrapper}
                            mailboxItem={this.props.officeWrapper.currentContextItem}
                            isFilePathInAttachmentsSupported={this.props.isFilePathInAttachmentsSupported}
                            isNL1221Supported={this.props.showFileTransferButton}
                            logger={this.props.logger}
                            formValidationHelpers={this.props.formValidationHelpers}
                            isOpen={this.state.showFileTransferPanel}
                            onClose={() => this.closeFileTransferClicked()}
                            configService={this.props.configService}
                            onShowToast={this.props.onShowToast}
                            msGraphApiService={this.props.msGraphApiService}
                            isSendFilesFromProjectSupported={this.props.isSendFilesFromProjectSupported}
                            isGlobalProjectSearchSupported={this.props.isGlobalProjectSearchSupported}
                            storageWrapper={this.props.storageWrapper}
                        />
                    </ProjectsProvider>
                </FluentProvider>
                {this.renderFilesTransferTeachingBubble()}
                {this.renderProjectItemTeachingBubble()}
            </div>
        );
    }
}

export default withLocalize(AppHeaderComponent);
